/**
 * Centralized Mock Data Utilities
 * 
 * This file contains all mock data generators and static mock data
 * used across the application. Centralizing mock data makes it easier to:
 * 1. Replace with real API data in the future
 * 2. Maintain consistency across components
 * 3. Test components in isolation
 * 
 * Usage:
 * import { generateDemoTasks, getMockCampaigns } from '@/utils/mockData';
 */

// ============================================================================
// TASK MOCK DATA
// ============================================================================

/**
 * Generate demo tasks based on role and industry
 * Used by: UnifiedDashboard
 */
export const generateDemoTasks = (role, industry = 'hospitality') => {
  const urgentTasks = {
    owner: [
      { id: 1, title: 'Review monthly P&L report', priority: 'high', deadline: 'Today', status: 'pending', category: 'financial', progress: 0 },
      { id: 2, title: 'Approve staff roster for next week', priority: 'medium', deadline: '2 days', status: 'in-progress', category: 'operations', progress: 60 },
      { id: 3, title: 'Meeting with suppliers', priority: 'medium', deadline: '3 days', status: 'pending', category: 'procurement', progress: 0 }
    ],
    'venue-operator': [
      { id: 1, title: 'Check inventory levels', priority: 'high', deadline: 'Today', status: 'pending', category: 'inventory', progress: 0 },
      { id: 2, title: 'Staff performance reviews due', priority: 'medium', deadline: '3 days', status: 'in-progress', category: 'hr', progress: 40 },
      { id: 3, title: 'Update menu pricing', priority: 'low', deadline: '1 week', status: 'pending', category: 'operations', progress: 0 }
    ],
    advisor: [
      { id: 1, title: 'Client quarterly review - ABC Corp', priority: 'high', deadline: 'Today', status: 'pending', category: 'client', progress: 0 },
      { id: 2, title: 'Prepare valuation report', priority: 'high', deadline: '2 days', status: 'in-progress', category: 'analysis', progress: 75 },
      { id: 3, title: 'Market research update', priority: 'medium', deadline: '5 days', status: 'pending', category: 'research', progress: 0 }
    ],
    investor: [
      { id: 1, title: 'Review Q3 portfolio performance', priority: 'high', deadline: 'Today', status: 'pending', category: 'portfolio', progress: 0 },
      { id: 2, title: 'Due diligence - New opportunity', priority: 'high', deadline: '3 days', status: 'in-progress', category: 'analysis', progress: 50 },
      { id: 3, title: 'Board meeting preparation', priority: 'medium', deadline: '1 week', status: 'pending', category: 'governance', progress: 0 }
    ]
  };
  
  return urgentTasks[role] || urgentTasks.owner;
};

/**
 * Generate task KPI recommendations
 * Used by: UnifiedDashboard
 */
export const generateRecommendations = (role, industry = 'hospitality') => {
  const recommendations = {
    hospitality: [
      { id: 1, title: 'Optimize staff scheduling', description: 'Your Thursday evening shifts are overstaffed by 15%', impact: 'high', type: 'efficiency', savings: '$2,400/month' },
      { id: 2, title: 'Reduce food waste', description: 'Food cost increased 3% - consider portion control audit', impact: 'medium', type: 'cost', savings: '$1,800/month' },
      { id: 3, title: 'Boost slow nights', description: 'Tuesday revenue is 40% below average - consider promotions', impact: 'high', type: 'revenue', potential: '+$3,200/month' }
    ],
    retail: [
      { id: 1, title: 'Optimize inventory turnover', description: 'Category A items have 45-day average vs 30-day target', impact: 'high', type: 'efficiency', savings: '$4,200/month' },
      { id: 2, title: 'Improve conversion rate', description: 'Store conversion is 2.1% vs industry average of 2.8%', impact: 'high', type: 'revenue', potential: '+$6,500/month' },
      { id: 3, title: 'Reduce shrinkage', description: 'Shrinkage at 1.8% vs target 1.2%', impact: 'medium', type: 'cost', savings: '$2,100/month' }
    ],
    healthcare: [
      { id: 1, title: 'Reduce wait times', description: 'Average wait time is 23 mins vs target 15 mins', impact: 'high', type: 'efficiency' },
      { id: 2, title: 'Improve appointment utilization', description: 'No-show rate at 12% - implement reminder system', impact: 'high', type: 'revenue', potential: '+$8,000/month' },
      { id: 3, title: 'Optimize supply ordering', description: 'Medical supplies showing 20% variance from forecast', impact: 'medium', type: 'cost', savings: '$3,500/month' }
    ],
    professional: [
      { id: 1, title: 'Increase billable utilization', description: 'Team utilization at 68% vs target 75%', impact: 'high', type: 'revenue', potential: '+$15,000/month' },
      { id: 2, title: 'Reduce WIP aging', description: 'Average WIP age is 45 days vs target 30 days', impact: 'medium', type: 'cashflow' },
      { id: 3, title: 'Improve fee collection', description: 'Debtor days at 52 vs industry average 38', impact: 'high', type: 'cashflow' }
    ]
  };
  
  return recommendations[industry] || recommendations.hospitality;
};

// ============================================================================
// MARKETING MOCK DATA
// ============================================================================

/**
 * Get mock marketing campaigns
 * Used by: MarketingTab
 */
export const getMockCampaigns = () => [
  {
    id: '1',
    name: 'Summer Sale 2024',
    type: 'email',
    status: 'active',
    performance: { sent: 5000, opened: 2350, clicked: 890, converted: 125 },
    budget: 500,
    spent: 320,
    startDate: '2024-01-15',
    endDate: '2024-02-15'
  },
  {
    id: '2',
    name: 'New Menu Launch',
    type: 'social',
    status: 'active',
    performance: { impressions: 45000, clicks: 2100, engagement: 8.5 },
    budget: 1200,
    spent: 890,
    startDate: '2024-01-20',
    endDate: '2024-02-28'
  },
  {
    id: '3',
    name: 'Loyalty Rewards',
    type: 'email',
    status: 'completed',
    performance: { sent: 8500, opened: 4250, clicked: 1680, converted: 340 },
    budget: 300,
    spent: 300,
    startDate: '2024-01-01',
    endDate: '2024-01-14'
  },
  {
    id: '4',
    name: 'Google Ads - Local',
    type: 'ppc',
    status: 'paused',
    performance: { impressions: 120000, clicks: 3600, conversions: 180, costPerClick: 1.25 },
    budget: 2000,
    spent: 1450,
    startDate: '2024-01-10',
    endDate: '2024-03-10'
  }
];

// ============================================================================
// COMPETITOR MOCK DATA
// ============================================================================

/**
 * Get mock competitor analysis data
 * Used by: CompetitorAnalysisDashboard
 */
export const getMockCompetitorData = () => ({
  competitors: [
    {
      id: 1,
      name: 'Competitor A',
      marketShare: 25,
      priceIndex: 98,
      customerRating: 4.2,
      socialFollowers: 15000,
      strengths: ['Brand recognition', 'Location', 'Menu variety'],
      weaknesses: ['Service speed', 'Parking', 'Wait times']
    },
    {
      id: 2,
      name: 'Competitor B',
      marketShare: 18,
      priceIndex: 105,
      customerRating: 4.5,
      socialFollowers: 22000,
      strengths: ['Quality', 'Ambiance', 'Online presence'],
      weaknesses: ['Price point', 'Limited hours', 'Small portions']
    },
    {
      id: 3,
      name: 'Competitor C',
      marketShare: 12,
      priceIndex: 92,
      customerRating: 3.9,
      socialFollowers: 8000,
      strengths: ['Value pricing', 'Fast service', 'Family friendly'],
      weaknesses: ['Quality perception', 'Limited menu', 'Dated decor']
    }
  ],
  marketTrends: [
    { month: 'Jan', yourShare: 15, avgCompetitor: 18 },
    { month: 'Feb', yourShare: 16, avgCompetitor: 17 },
    { month: 'Mar', yourShare: 17, avgCompetitor: 17 },
    { month: 'Apr', yourShare: 18, avgCompetitor: 16 },
    { month: 'May', yourShare: 19, avgCompetitor: 16 },
    { month: 'Jun', yourShare: 20, avgCompetitor: 15 }
  ],
  insights: [
    { type: 'opportunity', text: 'Competitor A has reduced hours - opportunity to capture evening traffic' },
    { type: 'threat', text: 'Competitor B launching loyalty program next month' },
    { type: 'trend', text: 'Overall market growing 8% YoY in your category' }
  ]
});

// ============================================================================
// ADS MOCK DATA
// ============================================================================

/**
 * Get mock best performing ads
 * Used by: OnlineAdsDashboard
 */
export const getMockBestAds = () => [
  {
    id: 1,
    name: 'Summer Special - Search',
    platform: 'Google Ads',
    impressions: 45000,
    clicks: 2250,
    ctr: 5.0,
    conversions: 180,
    conversionRate: 8.0,
    spend: 1125,
    revenue: 5400,
    roas: 4.8
  },
  {
    id: 2,
    name: 'Brand Awareness - Display',
    platform: 'Google Ads',
    impressions: 120000,
    clicks: 4800,
    ctr: 4.0,
    conversions: 240,
    conversionRate: 5.0,
    spend: 960,
    revenue: 4800,
    roas: 5.0
  },
  {
    id: 3,
    name: 'Local Targeting - Facebook',
    platform: 'Facebook',
    impressions: 85000,
    clicks: 3400,
    ctr: 4.0,
    conversions: 170,
    conversionRate: 5.0,
    spend: 850,
    revenue: 3400,
    roas: 4.0
  }
];

/**
 * Get mock worst performing ads
 * Used by: OnlineAdsDashboard
 */
export const getMockWorstAds = () => [
  {
    id: 4,
    name: 'Generic Campaign',
    platform: 'Google Ads',
    impressions: 30000,
    clicks: 300,
    ctr: 1.0,
    conversions: 6,
    conversionRate: 2.0,
    spend: 450,
    revenue: 180,
    roas: 0.4,
    issue: 'Low CTR - ad copy needs improvement'
  },
  {
    id: 5,
    name: 'Old Promo - Instagram',
    platform: 'Instagram',
    impressions: 25000,
    clicks: 250,
    ctr: 1.0,
    conversions: 5,
    conversionRate: 2.0,
    spend: 375,
    revenue: 150,
    roas: 0.4,
    issue: 'Outdated creative - refresh needed'
  }
];

/**
 * Get mock ad recommendations
 * Used by: OnlineAdsDashboard
 */
export const getMockAdRecommendations = () => [
  {
    id: 1,
    title: 'Increase Budget on Top Performer',
    description: 'Summer Special campaign has 4.8x ROAS - consider 50% budget increase',
    impact: 'high',
    estimatedRevenue: '+$2,700/month'
  },
  {
    id: 2,
    title: 'Pause Generic Campaign',
    description: 'Only 0.4x ROAS - reallocate $450 budget to top performers',
    impact: 'high',
    estimatedSavings: '$450/month'
  },
  {
    id: 3,
    title: 'Test Video Ads',
    description: 'Video ads showing 2.3x higher engagement in your industry',
    impact: 'medium',
    estimatedRevenue: '+$1,500/month'
  }
];

// ============================================================================
// SEO MOCK DATA
// ============================================================================

/**
 * Get mock SEO dashboard data
 * Used by: SEODashboard
 */
export const getMockSeoData = () => ({
  overview: {
    domainAuthority: 35,
    organicTraffic: 4500,
    organicTrafficChange: 12,
    keywords: 156,
    keywordsChange: 8,
    backlinks: 234,
    backlinksChange: 15
  },
  topKeywords: [
    { keyword: 'best restaurant near me', position: 3, volume: 2400, change: 2 },
    { keyword: 'fine dining melbourne', position: 5, volume: 1800, change: -1 },
    { keyword: 'italian restaurant cbd', position: 2, volume: 1200, change: 3 },
    { keyword: 'romantic dinner melbourne', position: 8, volume: 900, change: 5 },
    { keyword: 'business lunch venue', position: 4, volume: 600, change: 0 }
  ],
  trafficTrend: [
    { month: 'Jan', organic: 3200, paid: 1800 },
    { month: 'Feb', organic: 3500, paid: 1600 },
    { month: 'Mar', organic: 3800, paid: 1900 },
    { month: 'Apr', organic: 4100, paid: 2100 },
    { month: 'May', organic: 4300, paid: 1700 },
    { month: 'Jun', organic: 4500, paid: 2000 }
  ],
  recommendations: [
    { title: 'Optimize meta descriptions', impact: 'medium', effort: 'low' },
    { title: 'Add schema markup for menu', impact: 'high', effort: 'medium' },
    { title: 'Build local citations', impact: 'high', effort: 'high' }
  ]
});

// ============================================================================
// WEBSITE HEALTH MOCK DATA
// ============================================================================

/**
 * Get mock website health data
 * Used by: WebsiteHealthDashboard
 */
export const getMockWebsiteHealthData = () => ({
  score: 78,
  scoreChange: 5,
  metrics: {
    performance: { score: 72, label: 'Performance', issues: 3 },
    accessibility: { score: 85, label: 'Accessibility', issues: 2 },
    seo: { score: 82, label: 'SEO', issues: 4 },
    security: { score: 95, label: 'Security', issues: 0 }
  },
  pageSpeed: {
    mobile: { score: 65, fcp: 2.8, lcp: 4.2, cls: 0.15 },
    desktop: { score: 82, fcp: 1.2, lcp: 2.1, cls: 0.05 }
  },
  issues: [
    { type: 'critical', title: 'Large images need compression', page: '/gallery', impact: 'high' },
    { type: 'warning', title: 'Missing alt text on 12 images', page: 'Multiple', impact: 'medium' },
    { type: 'info', title: 'Consider lazy loading for below-fold images', page: '/', impact: 'low' }
  ],
  uptime: {
    current: 99.95,
    last30Days: [
      { date: '2024-01-01', uptime: 100 },
      { date: '2024-01-08', uptime: 99.8 },
      { date: '2024-01-15', uptime: 100 },
      { date: '2024-01-22', uptime: 99.9 },
      { date: '2024-01-29', uptime: 100 }
    ]
  }
});

// ============================================================================
// CUSTOMER NOTES MOCK DATA
// ============================================================================

/**
 * Get mock customer notes
 * Used by: CustomerAssistancePanel
 */
export const getMockCustomerNotes = () => [
  {
    id: '1',
    content: 'Customer prefers window seating when available.',
    author: 'Sarah M.',
    date: '2024-01-15',
    type: 'preference'
  },
  {
    id: '2',
    content: 'Has a severe nut allergy - always confirm with kitchen.',
    author: 'John D.',
    date: '2024-01-10',
    type: 'allergy',
    priority: 'high'
  },
  {
    id: '3',
    content: 'Regular customer - celebrates anniversary here every year.',
    author: 'Mike T.',
    date: '2023-12-20',
    type: 'vip'
  }
];

// ============================================================================
// NOTIFICATION MOCK DATA
// ============================================================================

/**
 * Generate system notifications (minimal set for supplementary alerts)
 * Used by: NotificationsCenter
 */
export const generateSystemNotifications = (industry) => {
  const systemNotifications = [
    { id: 3, type: 'kpi', title: 'KPI Alert', message: 'Daily revenue target achieved - 112% of goal!', time: '30m ago', read: false, priority: 'low', route: '/app/management' },
    { id: 5, type: 'alert', title: 'System Alert', message: 'Low inventory warning: 3 items need reorder', time: '2h ago', read: true, priority: 'medium', route: '/app/management' },
    { id: 9, type: 'insight', title: 'AI Recommendation', message: 'Optimize staffing: Reduce Thursday evening shift by 1', time: '6h ago', read: true, priority: 'medium', route: '/app' }
  ];
  
  const industrySpecific = {
    hospitality: { id: 101, type: 'alert', title: 'Reservation Alert', message: 'Tonight fully booked - consider opening extra tables', time: '10m ago', read: false, priority: 'high', route: '/app/live-operations' },
    retail: { id: 101, type: 'alert', title: 'Stock Alert', message: 'Popular item running low', time: '10m ago', read: false, priority: 'high', route: '/app/live-operations' },
    healthcare: { id: 101, type: 'alert', title: 'Appointment Alert', message: 'Open appointment slots available', time: '10m ago', read: false, priority: 'high', route: '/app/live-operations' },
    professional: { id: 101, type: 'alert', title: 'Deadline Alert', message: 'Client proposal due tomorrow', time: '10m ago', read: false, priority: 'high', route: '/app/live-operations' }
  };
  
  const specific = industrySpecific[industry] || industrySpecific.hospitality;
  return [specific, ...systemNotifications];
};

// ============================================================================
// TEAM MOCK DATA
// ============================================================================

/**
 * Generate team members for dashboard
 * Used by: UnifiedDashboard
 */
export const generateTeamMembers = () => [
  { id: 1, name: 'Sarah Johnson', role: 'Manager', status: 'online', performance: 95, avatar: null },
  { id: 2, name: 'Mike Chen', role: 'Senior Server', status: 'online', performance: 88, avatar: null },
  { id: 3, name: 'Emily Davis', role: 'Server', status: 'busy', performance: 82, avatar: null },
  { id: 4, name: 'James Wilson', role: 'Kitchen Lead', status: 'online', performance: 91, avatar: null },
  { id: 5, name: 'Lisa Brown', role: 'Host', status: 'offline', performance: 78, avatar: null }
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format time ago from date string
 */
export const formatTimeAgo = (dateString) => {
  if (!dateString) return 'Unknown';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

/**
 * Generate random ID
 */
export const generateId = () => Math.random().toString(36).substr(2, 9);

// ============================================================================
// DASHBOARD-COMPATIBLE GENERATORS
// ============================================================================
// These generators return data structures compatible with UnifiedDashboard
// They use icon string identifiers that components can map to actual icons

/**
 * Icon mapping for recommendations
 * Components should import icons and use this mapping
 */
export const RECOMMENDATION_ICONS = {
  TrendingUp: 'TrendingUp',
  TrendingDown: 'TrendingDown',
  AlertTriangle: 'AlertTriangle',
  Users: 'Users',
  DollarSign: 'DollarSign',
  Sparkles: 'Sparkles',
  CheckCircle2: 'CheckCircle2',
  Target: 'Target',
  Clock: 'Clock'
};

/**
 * Generate AI recommendations with icon identifiers
 * @param {string} role - User role (owner, manager, etc.)
 * @param {string} industry - Industry type
 * @returns {Array} Recommendations with iconType strings
 */
export const generateDashboardRecommendations = (role, industry = 'hospitality') => {
  const recommendations = {
    hospitality: {
      owner: [
        { type: 'revenue', iconType: 'TrendingUp', priority: 'high', title: 'Revenue Opportunity', message: 'Tuesday lunch service is underperforming by 18%. Consider a mid-week promotion to boost covers.', action: 'View Analysis' },
        { type: 'cost', iconType: 'AlertTriangle', priority: 'high', title: 'Food Cost Alert', message: 'Food costs at 31% - 3% above target. Review portion sizes and supplier pricing.', action: 'View Costs' },
        { type: 'staff', iconType: 'Users', priority: 'medium', title: 'Staffing Efficiency', message: 'Saturday evening is overstaffed by 2 positions based on booking trends.', action: 'Adjust Roster' }
      ],
      manager: [
        { type: 'inventory', iconType: 'AlertTriangle', priority: 'high', title: 'Stock Alert', message: 'Salmon and avocado running low. Reorder recommended before Friday.', action: 'Order Stock' },
        { type: 'waste', iconType: 'TrendingUp', priority: 'medium', title: 'Waste Reduction', message: 'Prep waste down 12% this week. Great job on portion control!', action: 'View Details' },
        { type: 'menu', iconType: 'Sparkles', priority: 'low', title: 'Menu Insight', message: 'Vegetarian dishes up 25% this month. Consider expanding plant-based options.', action: 'View Trends' }
      ]
    },
    retail: {
      owner: [
        { type: 'sales', iconType: 'TrendingUp', priority: 'high', title: 'Sales Opportunity', message: 'Accessories category conversion is 40% below benchmark. Train staff on add-on sales.', action: 'View Training' },
        { type: 'inventory', iconType: 'AlertTriangle', priority: 'high', title: 'Stock Alert', message: '15 SKUs below reorder point. Review and place orders today.', action: 'View Stock' },
        { type: 'shrinkage', iconType: 'AlertTriangle', priority: 'medium', title: 'Shrinkage Alert', message: 'Shrinkage trending 0.3% above target. Review security footage.', action: 'View Report' }
      ]
    },
    healthcare: {
      owner: [
        { type: 'utilization', iconType: 'TrendingUp', priority: 'high', title: 'Utilization Gap', message: 'Dr. Smith has 3 open slots tomorrow. Consider promotion or waitlist outreach.', action: 'View Schedule' },
        { type: 'rebooking', iconType: 'AlertTriangle', priority: 'medium', title: 'Rebooking Rate', message: 'Rebooking rate dropped 8% this week. Review checkout process.', action: 'View Analysis' },
        { type: 'billing', iconType: 'DollarSign', priority: 'high', title: 'Billing Alert', message: '12 claims pending over 30 days. Follow up required.', action: 'View Claims' }
      ]
    },
    fitness: {
      owner: [
        { type: 'retention', iconType: 'AlertTriangle', priority: 'high', title: 'Retention Risk', message: '23 members haven\'t visited in 14+ days. Trigger win-back campaign.', action: 'View Members' },
        { type: 'classes', iconType: 'TrendingUp', priority: 'medium', title: 'Class Opportunity', message: 'Yoga classes at 95% capacity. Consider adding more sessions.', action: 'View Schedule' },
        { type: 'pt', iconType: 'DollarSign', priority: 'high', title: 'PT Revenue', message: 'PT revenue down 12% this month. Review trainer availability.', action: 'View PT Stats' }
      ]
    },
    technology: {
      owner: [
        { type: 'churn', iconType: 'AlertTriangle', priority: 'high', title: 'Churn Risk', message: '8 enterprise accounts showing reduced usage. Schedule health checks.', action: 'View Accounts' },
        { type: 'growth', iconType: 'TrendingUp', priority: 'high', title: 'Growth Opportunity', message: 'Trial-to-paid conversion up 15%. Capitalize with targeted campaigns.', action: 'View Funnel' },
        { type: 'support', iconType: 'AlertTriangle', priority: 'medium', title: 'Support SLA', message: 'Average response time at 4.2 hours - above 4hr SLA.', action: 'View Tickets' }
      ]
    }
  };
  
  const roleRecs = recommendations[industry]?.[role] || recommendations[industry]?.owner || [
    { type: 'performance', iconType: 'TrendingUp', priority: 'medium', title: 'Performance Tip', message: 'Your metrics are trending up. Keep the momentum!', action: 'View Stats' },
    { type: 'task', iconType: 'CheckCircle2', priority: 'low', title: 'Task Reminder', message: 'You have pending tasks for today.', action: 'View Tasks' }
  ];
  
  return roleRecs;
};

/**
 * Generate team members for dashboard team view
 */
export const generateDashboardTeamMembers = () => {
  return [
    { id: 'tm_1', name: 'Sarah Chen', role: 'Kitchen Manager', avatar: null, performance: 92, tasksCompleted: 18, tasksPending: 3, status: 'on-track', trend: 5 },
    { id: 'tm_2', name: 'James Wilson', role: 'FOH Manager', avatar: null, performance: 88, tasksCompleted: 15, tasksPending: 5, status: 'on-track', trend: 3 },
    { id: 'tm_3', name: 'Maria Garcia', role: 'Bar Manager', avatar: null, performance: 78, tasksCompleted: 12, tasksPending: 8, status: 'at-risk', trend: -2 },
    { id: 'tm_4', name: 'Tom Baker', role: 'Shift Supervisor', avatar: null, performance: 95, tasksCompleted: 22, tasksPending: 2, status: 'exceeding', trend: 8 },
    { id: 'tm_5', name: 'Lisa Park', role: 'Accountant', avatar: null, performance: 85, tasksCompleted: 10, tasksPending: 4, status: 'on-track', trend: 1 },
    { id: 'tm_6', name: 'David Lee', role: 'Shift Supervisor', avatar: null, performance: 82, tasksCompleted: 14, tasksPending: 6, status: 'on-track', trend: 4 }
  ];
};
